import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Tooltip,
  Typography,
} from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { NonNullableItemState } from "../../redux/state";
import {
  AppBarConfirmButton,
  AppBarSaveButton,
  AppBarDirtyIndicator,
} from "@ofqual/portal-components/components/SecondaryAppBar";
import { Select } from "@ofqual/portal-components/components/Select";
import {
  InformationRequest,
  InformationRequestStatus,
} from "../../domain/InformationRequest";
import { ModuleAppBar } from "../ModuleAppBar";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import {
  requestTypeLookupService,
  tagsService,
  requestSubTypeLookupService,
} from "../../services";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { ItemType } from "@ofqual/portal-components/components/types";
import { useResetOnModelUpdate } from "@ofqual/portal-core/core/hooks";
import { toGroupedList } from "@ofqual/portal-core/core/utils/listHelpers";

import {
  buildMetaData,
  getLookupTracker,
  modelToJsonPatchDocument,
} from "@ofqual/portal-core/core/utils/modelHelpers";
import { updatingItem, sendingItem, deletingItem } from "../../redux/actions";
import { fieldLabels } from "./fieldLabels";
import { Delete, Info } from "@material-ui/icons";
import { Permissions } from "./permissions/Permissions";
import { useValidation } from "../../hooks/useValidation";

import RouteLeavingGuard from "../common/RouteLeavingGuard";
import { useHistory } from "react-router-dom";
import { authSelector } from "../../redux/selectors";
import { ExperimentalFeature } from "@ofqual/portal-components/components/ExperimentalFeature";

const useStyles = makeStyles((theme) => ({
  formSection: {
    padding: 30,
    boxShadow: "0 0 19px -2px hsla(0,0%,63.9%,.3)",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    borderRadius: "3px",
  },
  infoIcon: {
    margin: 10,
    fontSize: "2em",
  },
}));

type FormProps = {
  state: NonNullableItemState;
};

export const Form: React.FC<FormProps> = ({ state }) => {
  const { status, item } = state;

  const { handleSubmit, control, formState, reset } =
    useForm<InformationRequest>({
      defaultValues: item,
    });

  const { experimentalFeatures } = useSelector(authSelector);

  useResetOnModelUpdate(item, reset);

  const lookupTracker = useMemo(() => getLookupTracker(), []);

  const dispatch = useDispatch();
  const canEdit = status === "LOADED";

  const [canLeave, setCanLeave] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // this is important for dirtyFields to be deconstructed/accessed prior to the onSubmit process
  const { dirtyFields } = formState;

  const onSubmit = (model: InformationRequest) => {
    const changedFields = Object.keys(
      dirtyFields
    ) as (keyof InformationRequest)[];

    let document = modelToJsonPatchDocument(model, changedFields);
    const resourceTagPatch = document.find(
      (patch) => patch.path === "/resourceTags"
    );
    let resourceTagIds: number[] = item.resourceTags;
    if (resourceTagPatch) {
      resourceTagIds = resourceTagPatch.value;
      document = document.filter((x) => x !== resourceTagPatch);
    }
    const meta = buildMetaData(model, item, changedFields, lookupTracker);
    dispatch(updatingItem(item.id, { document, meta }, resourceTagIds));
  };

  const [requestTypeLookups, tagsLookups, requestSubTypeLookups] = useLookup(
    requestTypeLookupService,
    {
      service: tagsService,
      id: "id",
      label: "tag",
    },
    requestSubTypeLookupService
  );

  const groupedTagsLookup = toGroupedList(tagsLookups, {
    id: "id",
    label: "tag",
    group: "tagCategory",
  });

  const deleteCaoAction = () => {
    setDeleting(true);
    dispatch(deletingItem(item.id));
  };

  const classes = useStyles();
  const isDirty = dirtyFields && Object.keys(dirtyFields).length;

  const { isReady, isOk } = useValidation();

  const canSend = canEdit && !isDirty && isReady && isOk;
  const isSent = item.status === InformationRequestStatus.Sent;

  const { push } = useHistory();

  useEffect(() => {
    if (!!item && item.status !== InformationRequestStatus.New) {
      setCanLeave(true);
    }
  }, [item, setCanLeave]);

  useEffect(() => {
    if (deleting && canLeave) {
      dispatch(deletingItem(item.id));
      push(`/communications/contact-ao`);
    }
  }, [deleting, canLeave, dispatch, push, item]);

  return (
    <>
      <RouteLeavingGuard
        when={!canLeave}
        navigate={push}
        shouldBlockNavigation={() => true}
        actionOnUnload={deleteCaoAction}
      />

      <ModuleAppBar
        breadcrumbs={[
          {
            label: state.item?.subject ? state.item?.subject : "New",
          },
        ]}
      >
        <AppBarDirtyIndicator dirtyFields={dirtyFields} />
        <AppBarSaveButton
          onClick={handleSubmit(onSubmit)}
          isSubmitting={state.status === "UPDATING"}
          isSubmitted={state.lastCrudResult.isSuccess}
          disabled={!canEdit}
          dataTest="save-button"
        />

        {!isSent ? (
          <AppBarConfirmButton
            text="Send"
            disabled={!canSend}
            onClick={() => dispatch(sendingItem(item.id))}
            dataTest="send-button"
          >
            Are you sure you wish to Send this request?
          </AppBarConfirmButton>
        ) : null}

        {!isSent ? (
          <AppBarConfirmButton
            Icon={Delete}
            text="Delete"
            disabled={isSent}
            onClick={() => {
              setCanLeave(true);
              setDeleting(true);
            }}
            dataTest="send-button"
          >
            Are you sure you wish to <strong>Delete</strong> this request?
          </AppBarConfirmButton>
        ) : null}
      </ModuleAppBar>
      <form>
        <Typography variant="h6">Message Details</Typography>
        <Paper className={classes.formSection}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center">
                <Box width="100%">
                  <Controller
                    control={control}
                    name="requestType"
                    as={
                      <Select
                        label={fieldLabels.requestType}
                        disabled={!canEdit}
                        readOnly={isSent}
                        lookupTracker={lookupTracker}
                        data-test="cao-requestType"
                      >
                        {requestTypeLookups}
                      </Select>
                    }
                  />
                </Box>
                <Box>
                  <Tooltip
                    title="Some request types require additional actions to be created before requests can be sent"
                    placement="top"
                  >
                    <Button>
                      <Info className={classes.infoIcon} color="secondary" />
                    </Button>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
            <ExperimentalFeature
              experimentalModuleName="portal-comms-cao"
              experimentalFeatures={experimentalFeatures}
              experimentalFeatureVisibleName="cao-request-sub-type"
            >
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="requestSubType"
                  as={
                    <Select
                      label={fieldLabels.requestSubType}
                      disabled={!canEdit}
                      readOnly={isSent}
                      lookupTracker={lookupTracker}
                      data-test="cao-requestSubType"
                    >
                      {requestSubTypeLookups}
                    </Select>
                  }
                />
              </Grid>
            </ExperimentalFeature>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="subject"
                as={
                  <TextField
                    label={fieldLabels.subject}
                    readOnly={!canEdit}
                    multiline
                    data-test="cao-subject"
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="message"
                render={({ onChange, value }) => (
                  <>
                    <label>Message:</label>
                    <MarkdownEditor
                      onChange={onChange}
                      value={value}
                      dataTest="new-message-field"
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="resourceTags"
                as={
                  <Select
                    selectVariant="checkboxes"
                    label={fieldLabels.resourceTags}
                    datatest="tags"
                    disabled={!canEdit}
                    lookupTracker={lookupTracker}
                  >
                    {groupedTagsLookup as ItemType[]}
                  </Select>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="allowAoReassignment"
                render={(props) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) => {
                          props.onChange(e.target.checked);
                        }}
                        checked={props.value}
                        disabled={!canEdit}
                        data-test="ao-reassign-button"
                      />
                    }
                    label={fieldLabels.allowAoReassignment}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Paper>
        <Permissions control={control} lookupTracker={lookupTracker} />
      </form>
    </>
  );
};
